import {IsoDate} from "../../../pseudo-types/DateTypes";
import {PriceStrategy} from "../../../components/inputs/price/input/PriceStrategy";
import {Transform} from "../../../types/GenericTypes";
import {TimeSeriesPeriod} from "../types/TimeSeriesPeriod";
import {formatDate} from "../../../js/util/DateTimeUtil";
import moment from "moment";
import {sortTimeSeriesRows, TimeSeriesRecord, TimeSeriesRecords} from "../types/TimeSeriesRecord";
import {ascending, descending} from "../../../js/util/UtilFunctions";

export type TimeSeriesViewModels = ReadonlyArray<TimeSeriesViewModel>;

export interface TimeSeriesViewModel {
    readonly emptyRow: boolean;
    readonly hasHighestHigh: boolean;
    readonly hasLowestLow: boolean;
}

export class TimeSeriesEmptyViewModel implements TimeSeriesViewModel {
    public readonly emptyRow = true;
    public readonly hasHighestHigh = false;
    public readonly hasLowestLow = false;
}

const EmptyRow = new TimeSeriesEmptyViewModel();

export class TimeSeriesValueViewModel implements TimeSeriesViewModel {

    public constructor(public readonly date: string,
                       public readonly hasHighestHigh: boolean,
                       public readonly hasLowestLow: boolean,
                       public readonly change: number,
                       public readonly changePct: number,
                       public readonly row: TimeSeriesRecord,
                       public readonly priceStrategy: PriceStrategy) {
    }

    public get emptyRow(): boolean {
        return false;
    }

    public get open(): number {
        return this.row.open;
    }

    public get high(): number {
        return this.row.high;
    }

    public get low(): number {
        return this.row.low;
    }

    public get close(): number {
        return this.row.close;
    }

    public get volume(): number {
        return this.row.volume;
    }

    public get value(): number {
        return this.row.value;
    }

    public get tradeCount(): number {
        return this.row.tradeCount;
    }

    public get marketVwap(): number {
        return this.row.marketVwap;
    }

    public get shortSellPosition(): number {
        return this.row.shortSellPosition;
    }

    public get shortSellPercent(): number {
        return this.row.shortSellPercent;
    }

    public get shortSold(): number {
        return this.row.shortSold;
    }

    public get shortSoldPercent(): number {
        return this.row.shortSoldPercent;
    }
}

export function BuildViewModels(period: TimeSeriesPeriod,
                                data: TimeSeriesRecords,
                                priceStrategy: PriceStrategy): TimeSeriesViewModels {
    if (data.length === 0) {
        return [];
    }
    const highestHigh = data.map((d) => d.high).sort(descending)[0];
    const lowestLow = data.map((d) => d.low).sort(ascending)[0];
    const dataModel = [...data].sort(sortTimeSeriesRows())
                               .map((d, index, array) =>
                                        BuildViewModel(period, d, highestHigh, lowestLow, index, array, priceStrategy));
    const result: Array<TimeSeriesViewModel> = [];

    const modelPeriodFn = datePeriodCalculator(period);
    let previousPeriod: string | null = null;

    dataModel.forEach((m) => {
        const modelPeriod = modelPeriodFn(m.row.date);
        if ((previousPeriod != null) && (previousPeriod !== modelPeriod)) {
            result.push(EmptyRow);
        }
        previousPeriod = modelPeriod;
        result.push(m);
    });

    return result;
}

function BuildViewModel(period: TimeSeriesPeriod,
                        row: TimeSeriesRecord,
                        high: number,
                        low: number,
                        currentIndex: number,
                        array: Array<TimeSeriesRecord>,
                        priceStrategy: PriceStrategy): TimeSeriesValueViewModel {
    const previousRow = array[currentIndex + 1];
    const date = formatDateForPeriod(period, row.date);
    const change = rowChange(row, previousRow);
    const changePct = rowChangePct(row, previousRow);
    const hasHigh = high === row.high;
    const hasLow = low === row.low;
    return new TimeSeriesValueViewModel(date, hasHigh, hasLow, change, changePct, row, priceStrategy);
}

function formatDateForPeriod(period: TimeSeriesPeriod, date: IsoDate): string {
    switch (period) {
        case TimeSeriesPeriod.DAILY:
            return formatDate(date, "DD/MM/YYYY");
        case TimeSeriesPeriod.WEEKLY:
            return weekFormat(date);
        case TimeSeriesPeriod.MONTHLY:
            return formatDate(date, "MMM YYYY");
        case TimeSeriesPeriod.QUARTERLY:
            return formatDate(date, "MMM YYYY");
        case TimeSeriesPeriod.YEARLY:
            return formatDate(date, "YYYY");
    }
}

function weekFormat(date: IsoDate): string {
    const m = moment(date);
    const dayOfMonth = m.date();
    const week = Math.ceil(dayOfMonth / 7);
    const month = m.format("MMM YY");
    return `W${week} ${month}`;
}

function rowChange(row: TimeSeriesRecord, previousRow?: TimeSeriesRecord): number {
    if (previousRow == null) {
        if (row.close == null || row.open == null) {
            return 0;
        }
        return row.close - row.open;
    }
    return row.close - previousRow.close;
}

function rowChangePct(row: TimeSeriesRecord, previousRow?: TimeSeriesRecord): number {
    const change = rowChange(row, previousRow);
    if ((change === 0) || (previousRow == null) ) {
        if (row.open === null || row.open === 0) {
            return 0;
        }
        return change / row.open;
    } else {
        return change / previousRow.close;
    }
}

function datePeriodCalculator(period: TimeSeriesPeriod): Transform<IsoDate, string> {
    switch (period) {
        case TimeSeriesPeriod.DAILY:
            return weekFormat;
        case TimeSeriesPeriod.WEEKLY:
            return (d) => formatDate(d, "YYYYMM");
        case TimeSeriesPeriod.MONTHLY:
            return monthlyPeriodIdentifier;
        case TimeSeriesPeriod.QUARTERLY:
            return (d) => formatDate(d, "YYYY");
        case TimeSeriesPeriod.YEARLY:
            return (d) => "";
    }
}

function monthlyPeriodIdentifier(date: IsoDate): string {
    const year = formatDate(date, "YYYY");
    const half = Math.floor(moment(date).month() / 6);
    return `${year}${half}`;
}